/**
 * Info panel - displays asset/project metadata. No data processing logic.
 * Contains triggers for ProjectDetailEditor and AssetDetailsEditor child panels.
 */
import { Cartographic, Math as CesiumMath, Rectangle } from "cesium";
import { AssetGeoLocation, ConstruktedTransformEditors } from "../types/common";
import { BasePanel } from "./BasePanel";
import type { PanelContainer } from "./BasePanel";

export class InfoPanel extends BasePanel {
    private _container: ReturnType<typeof window.jQuery> | null = null;

    getId(): string {
        return "info";
    }

    getContainer(): PanelContainer {
        if (!this._container) {
            this._container = window.jQuery("#construkted-popup-info");
        }

        return this._container;
    }

    protected onInit(): void {
        const construkted = window.Construkted;
        const editAssetDetailsBtn = window.jQuery(".asset-details-editor-trigger");
        const editProjectDetailsBtn = window.jQuery(".project-details-editor-trigger");

        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get("open") === "details") {
            const currentPath = window.location.pathname;

            if (currentPath.includes("/asset/")) {
                editAssetDetailsBtn.trigger("click");
            } else if (currentPath.includes("/project/")) {
                editProjectDetailsBtn.trigger("click");
            }
        }

        if (construkted.isOrthomosaic()) {
            construkted.orthomoisaicViewer.orthomosaicAdded.addEventListener(() => {
                const rectangle = construkted.orthomoisaicViewer.asset.rectangle;
                const center = Rectangle.center(rectangle);
                const digit = 8;
                const longitude = CesiumMath.toDegrees(center.longitude).toFixed(digit);
                const latitude = CesiumMath.toDegrees(center.latitude).toFixed(digit);
                const jqAssetMetadataGeolocation = window.jQuery("#asset-metadata-geolocation");

                jqAssetMetadataGeolocation.html(
                    `Latitude: &nbsp;&nbsp; ${latitude} <br/> Longitude: &nbsp;${longitude}`
                );
            });

            return;
        }

        if (construkted.isTilesetAsset()) {
            this._initTilesetInfo(construkted, editAssetDetailsBtn);
        }

        if (construkted.isProject()) {
            editProjectDetailsBtn.on("click", () => {
                window.panelManager?.openPanelSync("project-detail-editor", { parentPanelId: "info" });
            });
        }
    }

    private _initTilesetInfo(construkted: any, editAssetDetailsBtn: ReturnType<typeof window.jQuery>): void {
        const masterAsset = construkted.assetViewer.masterAsset;
        let displayed = false;

        const displayGeolocation = (geolocation: AssetGeoLocation) => {
            const jqAssetMetadataGeolocation = window.jQuery("#asset-metadata-geolocation");
            const digit = 8;

            jqAssetMetadataGeolocation.html(
                `Latitude : &nbsp;&nbsp; ${geolocation?.latitude.toFixed(
                    digit
                )} <br/> Longitude: &nbsp;${geolocation?.longitude.toFixed(
                    digit
                )} <br/> Elevation : &nbsp;&nbsp;${geolocation?.height.toFixed(3)} `
            );
            displayed = true;
        };

        masterAsset.readyEvt.addEventListener(() => {
            if (masterAsset?.originallyGeoreferencedAtECEF && !displayed) {
                displayGeolocation(masterAsset.originalGeolocation());
            }
        });

        const jqAssetMetadataGeolocationContainer = window.jQuery("#asset-metadata-geolocation-container");

        const setVisibilityGeolocation = () => {
            if (window.CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.VisualPositionEditor) {
                if (window.CONSTRUKTED_AJAX.terrain_imagery_enabled) {
                    jqAssetMetadataGeolocationContainer.show();
                } else {
                    jqAssetMetadataGeolocationContainer.hide();
                }
            } else {
                jqAssetMetadataGeolocationContainer.show();
            }
        };

        const updateGeolocation = () => {
            setVisibilityGeolocation();

            const geolocation = masterAsset.calculateGeolocation();

            if (masterAsset.hasValidGeoRef()) {
                const carto = Cartographic.fromCartesian(masterAsset.geoRef);

                geolocation.height = carto.height;
            }

            displayGeolocation(geolocation);
        };

        masterAsset.geolocationChanged.addEventListener(() => updateGeolocation());
        masterAsset.geoRefChanged.addEventListener(() => updateGeolocation());

        editAssetDetailsBtn.on("click", () => {
            window.panelManager?.openPanelSync("asset-details-editor", { parentPanelId: "info" });
        });

        setVisibilityGeolocation();
    }
}
